<template>
  <div class="flex gap-6">
    <div class="w-56 shrink-0">
      <label class="btn btn-primary w-full" for="upload_version">
        <i class="fa-solid fa-cloud-arrow-up text-primary-content/90"></i>
        <span> Tải phiên bản mới </span>
      </label>
      <input
        class="sr-only focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        type="file"
        id="upload_version"
        @change="open_upload_modal"
      />
      <form
        ref="status_form"
        class="mb-4 space-y-4"
        @reset="keyword = undefined"
      >
        <fieldset class="fieldset">
          <legend class="fieldset-legend">Từ khóa</legend>
          <label class="input w-full md:w-fit 2xl:w-fit">
            <i class="fa-solid fa-magnifying-glass text-base-content/80"></i>
            <input
              type="text"
              placeholder="Tìm kiếm theo từ khóa"
              name="keyword"
              v-model="keyword"
            />
          </label>
          <p class="label">Chỉ áp dụng với tên của phiên bản</p>
        </fieldset>
      </form>

      <!-- ponytail: replaces the 12 per-type tabs; options derive from tabs[].be_mapping -->
      <fieldset class="fieldset">
        <legend class="fieldset-legend">Loại</legend>
        <div class="flex flex-wrap gap-1">
          <button
            v-for="opt in typeOptions"
            :key="opt.be_mapping"
            type="button"
            class="badge"
            :aria-pressed="
              (typeGroup.get(opt.be_mapping) ?? [opt.be_mapping]).some((t) =>
                selectedTypes.includes(t),
              )
            "
            :class="
              (typeGroup.get(opt.be_mapping) ?? [opt.be_mapping]).some((t) =>
                selectedTypes.includes(t),
              )
                ? 'badge-primary text-primary-content'
                : 'badge-outline'
            "
            @click="toggleType(opt.be_mapping)"
          >
            {{ opt.value }}
          </button>
        </div>
        <p class="label">Bỏ trống = hiển thị tất cả</p>
      </fieldset>
    </div>

    <div class="grow">
      <div class="text-base-content/80 mb-2 text-xl font-bold">
        Danh sách phiên bản
      </div>
      <Loading v-if="versionsQuery.isLoading.value" />
      <div
        v-else-if="
          versionsQuery.data.value?.total_filter &&
          versionsQuery.data.value.total_filter > 0
        "
        class="overflow-x-auto"
      >
        <table class="table-pin-rows table-md mb-4 table">
          <thead>
            <tr>
              <th>Phiên bản</th>
              <th>Loại</th>
              <th>MD5</th>
              <th>Mô tả</th>
              <th>Thời gian tạo</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody class="bg-base-200">
            <tr
              v-for="version in versionsQuery.data.value?.data"
              :key="version.id"
            >
              <td>{{ version.version }}</td>
              <td>{{ typeLabels.get(version.type) ?? version.type }}</td>
              <td>{{ version.md5 }}</td>
              <td>{{ version.description }}</td>
              <td>{{ dayjs(version.created).fromNow() }}</td>
              <td>
                <button
                  v-if="lolbinTypes.has(version.type) && version.public_path"
                  type="button"
                  class="btn btn-ghost btn-sm"
                  :aria-label="`Sao chép URL ${version.type}`"
                  @click="copyPublicPath(version.public_path)"
                >
                  <i class="fa-regular fa-copy" aria-hidden="true"></i>
                  Sao chép URL
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <Pagination :total_pages="versionsQuery.totalPages.value" />
      </div>
      <NotFound v-else />
    </div>
  </div>
  <ConfirmUploadVersion :file="upload_file" />
</template>

<script setup lang="ts">
import { array_from_query, str_from_query } from "@/api";
import { tabs } from "@/components/VersionControl";
import Loading from "@/components/LoadingTemplate.vue";
import NotFound from "@/components/NotFound.vue";
import Pagination from "@/components/PaginationTemplate.vue";
import { useVersionsQuery } from "@/composables/useVersions";
import { router } from "@/router/router";
import { watchDebounced } from "@vueuse/core";
import dayjs from "dayjs";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import ConfirmUploadVersion from "./ConfirmUploadVersion.vue";

const init_keyword = computed(() => str_from_query("keyword"));

const keyword = ref<string | undefined>(init_keyword.value);
const versionsQuery = useVersionsQuery();
const lolbinTypes = new Set([
  "lol_bin",
  "bitsadmin",
  "mshata",
  "certuil",
  "regsvr32",
]);

async function copyPublicPath(publicPath: string) {
  try {
    await navigator.clipboard.writeText(publicPath);
    toast.success("Đã sao chép URL");
  } catch {
    toast.error("Không thể sao chép URL");
  }
}

// ponytail: filter options derive from tabs[].be_mapping; x86/x64 later = add
// a tab in index.ts and it filters automatically. A chip covering several
// types (Kill AV) also contributes one filter chip per product (upload_types).
const typeOptions = tabs.flatMap((t) =>
  t.upload_types ?? (t.be_mapping ? [{ value: t.value, be_mapping: t.be_mapping }] : []),
);
const selectedTypes = computed(() => array_from_query("type", String));

// Aggregate chips map to every type they cover (e.g. kill_av -> 3 AV products).
const typeGroup = new Map(
  tabs.flatMap((t) =>
    t.be_mapping
      ? [[t.be_mapping, t.also_matches ?? [t.be_mapping]] as const]
      : [],
  ),
);
// Persisted sibling types (kill_av__win_def, kill_av_bit, kill_av_kas) get their
// product label from the chip's upload_types, so the table never shows raw ids.
const typeLabels = new Map(
  [
    ...tabs.flatMap((tab) => (tab.be_mapping ? [[tab.be_mapping, tab.value]] : [])),
    ...tabs.flatMap((tab) => (tab.upload_types ?? []).map((t) => [t.be_mapping, t.value] as const)),
  ] as [string, string][],
);

function toggleType(value: string) {
  const nextTypes = [...selectedTypes.value];
  const members = typeGroup.get(value) ?? [value];
  const i = nextTypes.findIndex((t) => members.includes(t));
  if (i >= 0) nextTypes.splice(i, 1);
  else nextTypes.push(...members);
  router.push({
    name: router.currentRoute.value.name,
    query: {
      ...router.currentRoute.value.query,
      page: 1,
      type: nextTypes.length ? nextTypes : undefined,
    },
  });
}
const upload_file = ref<File | undefined>(undefined);

// Use debounced watch to automatically update route when keyword changes
watchDebounced(
  () => keyword.value,
  (newKeyword) => {
    router.push({
      name: router.currentRoute.value.name,
      query: {
        ...router.currentRoute.value.query,
        page: 1,
        keyword: newKeyword,
      },
    });
  },
  { debounce: 500, maxWait: 1000 },
);

// Sync keyword from URL if it changes externally
watch(init_keyword, () => {
  if (keyword.value !== init_keyword.value) {
    keyword.value = init_keyword.value;
  }
});

const open_upload_modal = (event: Event) => {
  const upload_modal = document.getElementById(
    "confirm_upload_version",
  ) as HTMLDialogElement | null;
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (files && files.length > 0) {
    upload_file.value = files[0];
    if (upload_modal) upload_modal.showModal();
  }
};

// Clear selected file and reset input when the modal closes
let dialogCloseHandler: ((this: HTMLDialogElement, ev: Event) => void) | null =
  null;

onMounted(() => {
  const upload_modal = document.getElementById(
    "confirm_upload_version",
  ) as HTMLDialogElement | null;
  dialogCloseHandler = () => {
    upload_file.value = undefined;
    const input = document.getElementById(
      "upload_version",
    ) as HTMLInputElement | null;
    if (input) input.value = "";
  };
  if (upload_modal && dialogCloseHandler)
    upload_modal.addEventListener("close", dialogCloseHandler);
});

onBeforeUnmount(() => {
  const upload_modal = document.getElementById(
    "confirm_upload_version",
  ) as HTMLDialogElement | null;
  if (upload_modal && dialogCloseHandler)
    upload_modal.removeEventListener("close", dialogCloseHandler);
});
</script>
